/**
 * Utility functions for SDF algorithms.
 */

import { gl } from '../../../Engine';

/**
 * Reset GL capabilities to their expected default state.
 * Call this before yielding to the event loop during
 * long-running GL operations, and at the end of GL functions.
 *
 * This avoids expensive gl.getParameter() CPU-GPU sync points
 * that a save/restore pattern would require.
 */
export function resetGlDefaults(): void {
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.depthMask(true);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
}

/**
 * Reentrancy guard for async operations that use the shared
 * global WebGL context. Prevents overlapping GL operations
 * from corrupting each other's state.
 *
 * Usage:
 *   if (!GlOperationGuard.acquire('Gaussian smoothing')) return;
 *   try { ... } finally { GlOperationGuard.release(); }
 */
export class GlOperationGuard {
    private static activeOperation: string | null = null;

    /** Try to acquire the guard. Returns true if acquired. */
    public static acquire(operationName: string): boolean {
        if (GlOperationGuard.activeOperation) {
            console.error(
                `[GlOperationGuard] Cannot start "${operationName}": ` +
                    `"${GlOperationGuard.activeOperation}" is already running. ` +
                    `Concurrent async GL operations are not supported.`
            );
            return false;
        }
        GlOperationGuard.activeOperation = operationName;
        return true;
    }

    /** Release the guard. */
    public static release(): void {
        GlOperationGuard.activeOperation = null;
    }

    /** Check if a GL operation is currently running. */
    public static get isActive(): boolean {
        return GlOperationGuard.activeOperation !== null;
    }
}

/**
 * Yield to event loop based on time budget.
 * Call this periodically in long-running operations to keep the UI responsive.
 *
 * @param lastYieldTick The timestamp of the last yield (from performance.now())
 * @param signal Optional AbortSignal to check for cancellation
 * @param budget Time budget in ms before yielding (default: 100ms)
 * @returns The new lastYieldTick value to use for the next call
 */
export async function maybeYield(
    lastYieldTick: number,
    signal?: AbortSignal,
    budget: number = 100
): Promise<number> {
    if (signal?.aborted) {
        throw new DOMException('Operation aborted', 'AbortError');
    }
    const now = performance.now();
    if (now - lastYieldTick >= budget) {
        await new Promise<void>((r) => setTimeout(r, 0));
        return performance.now();
    }
    return lastYieldTick;
}
